// features/reports/components/EstadoResultados.tsx
// ============================================================================
// ESTADO DE RESULTADOS (bloque B1, 15-oct-2026) — sub-pestaña de Reportes › Pronóstico y presupuesto.
// Para Mabel: Ingresos − Costo docente = Utilidad bruta − Nómina administrativa − Capacitación − Operación −
// Marketing = Resultado OPERATIVO − Inversiones = Resultado NETO, con margen %, mes por mes y acumulado.
// No calcula nada: lo arma el servidor (/finances/presupuesto/resultados, lib/letsmoveResultados.js) reordenando
// «Por apartado», así que el neto ES el «Resultado» de Por apartado (el QA lo comprueba).
// Análisis: forobeta-ops/letsmove/ANALISIS-FINANZAS-MABEL-20261014.md (M5, B1).
// ============================================================================
import { Fragment, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, FileSpreadsheet, Info } from 'lucide-react';
import { getEstadoResultados, urlExportResultados, EstadoResultados as ER, ERFila } from '../api';
import { moneyEntero as money } from '../../finances/api';
import { Skeleton } from '../../../components/Skeleton';
import { readCache, writeCache } from '../../../services/localCache';

const card = 'rounded-2xl border border-line bg-surface p-4 shadow-sm';
const pctTxt = (p: number | null | undefined) => (p == null ? '' : `${p.toFixed(1)} %`);
const colorRes = (n: number) => (n >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400');
const navBtn = 'rounded-lg border border-line bg-surface p-2 text-content-2 transition hover:bg-surface-2';

function Kpi({ titulo, valor, margen, acum, margenAcum, rotulo, resultado }: { titulo: string; valor: number; margen?: number | null; acum: number; margenAcum?: number | null; rotulo: string; resultado?: boolean }) {
  return (
    <div className={card}>
      <div className="text-xs font-medium uppercase tracking-wide text-content-3">{titulo}</div>
      <div className={`mt-1 font-display text-xl font-bold tabular-nums ${resultado ? colorRes(valor) : 'text-content'}`}>{money(valor)}</div>
      <div className="mt-0.5 text-xs text-content-3">
        {margen != null && <span className="mr-1 font-semibold text-content-2">margen {pctTxt(margen)}</span>}
        <span className="block sm:inline">{rotulo.toLowerCase()}: {money(acum)}{margenAcum != null ? ` (${pctTxt(margenAcum)})` : ''}</span>
      </div>
    </div>
  );
}

// `desde` (trimestre/semestre, 24-oct-2026): columnas desde el 1.er mes del periodo; el acumulado es el total del periodo.
export default function EstadoResultados({ mes, desde = null }: { mes: string; desde?: string | null }) {
  const ck = `rep-resultados-${mes}${desde ? `-d${desde}` : ''}`;
  const [d, setD] = useState<ER | null>(() => readCache<ER>(ck) ?? null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<{ status: number; msg: string } | null>(null);
  const [abiertas, setAbiertas] = useState<Set<string>>(new Set());
  const [todos, setTodos] = useState(!!desde);   // en trimestre/semestre arranca «Mes por mes»
  const [verComo, setVerComo] = useState(false);
  const toggle = (k: string) => setAbiertas((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  useEffect(() => {
    let vivo = true;
    const cached = readCache<ER>(ck);
    setD(cached ?? null); setError(null); setCargando(!cached);
    getEstadoResultados(mes, desde)
      .then((r) => { if (!vivo) return; setD(r); writeCache(ck, r); })
      .catch((e: any) => { if (vivo) setError({ status: Number(e?.status || 0), msg: e?.message || 'No se pudo cargar el estado de resultados' }); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [mes, desde]);

  if (error && !d) {
    return (
      <div className={`${card} mt-4 text-sm`}>
        {error.status === 404
          ? <p className="flex items-center gap-2 text-content-2"><Info size={16} className="text-brand-blue" /> El estado de resultados se activa cuando el servidor termine de actualizarse. Vuelve a intentar más tarde.</p>
          : <p className="text-red-700 dark:text-red-300">{error.msg}</p>}
      </div>
    );
  }
  if (!d) return <Skeleton className="mt-4 h-96 w-full rounded-2xl" />;

  const sel = d.meses[d.meses.length - 1];
  const colSel = d.columnas[d.columnas.length - 1];
  const cols = todos ? d.columnas : [colSel];
  const fila = (k: string) => d.filas.find((f) => f.k === k) as ERFila;
  const ing = fila('ingresos'), bruta = fila('utilidadBruta'), oper = fila('operativo'), neto = fila('neto');
  const cuadra = Math.abs(sel.porApartado.resultado - neto.valores[sel.mes]) < 0.01;
  const pctDe = (v: number, base: number) => (base ? (100 * v) / base : null);
  const nColsNum = cols.length + 1;   // meses visibles + acumulado

  return (
    <div className={cargando ? 'opacity-60 transition' : 'transition'}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-content-3">
          <b className="text-content-2">Estado de resultados de {d.titulo.toLowerCase()}</b>{colSel.enCurso ? ' (mes en curso)' : ''} · cuánto quedó después de maestros, sueldos y gastos.
        </p>
        <div className="flex items-center gap-2">
          {d.columnas.length > 1 && (
            <label className="inline-flex items-center gap-1.5 text-xs text-content-3">
              <input type="checkbox" checked={todos} onChange={(e) => setTodos(e.target.checked)} /> Mes por mes
            </label>
          )}
          <a href={urlExportResultados(mes, desde)} download title="Descargar Excel del estado de resultados" className={`${navBtn} flex items-center gap-1.5 text-sm`}>
            <FileSpreadsheet size={16} /> <span className="hidden sm:inline">Excel</span>
          </a>
        </div>
      </div>

      {d.avisos.length > 0 && (
        <div className="mb-3 space-y-1 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          {d.avisos.map((a) => <p key={a} className="flex gap-1.5"><AlertTriangle size={13} className="mt-0.5 shrink-0" /> {a}</p>)}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi titulo="Ingresos" valor={ing.valores[sel.mes]} acum={ing.acumulado} rotulo={d.rotuloAcumulado} />
        <Kpi titulo="Utilidad bruta" valor={bruta.valores[sel.mes]} margen={bruta.margenes?.[sel.mes]} acum={bruta.acumulado} margenAcum={bruta.margenAcumulado} rotulo={d.rotuloAcumulado} resultado />
        <Kpi titulo="Resultado operativo" valor={oper.valores[sel.mes]} margen={oper.margenes?.[sel.mes]} acum={oper.acumulado} margenAcum={oper.margenAcumulado} rotulo={d.rotuloAcumulado} resultado />
        <Kpi titulo="Resultado neto" valor={neto.valores[sel.mes]} margen={neto.margenes?.[sel.mes]} acum={neto.acumulado} margenAcum={neto.margenAcumulado} rotulo={d.rotuloAcumulado} resultado />
      </div>

      <div className={`${card} mt-4 overflow-x-auto p-0`}>
        <table className="w-full text-sm" style={{ minWidth: todos ? 260 + 110 * nColsNum : undefined }}>
          <thead>
            <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-content-3">
              <th className="px-3 py-2 font-medium">Concepto</th>
              {cols.map((c) => (
                <th key={c.mes} className="px-2 py-2 text-right font-medium">
                  {c.titulo.replace(/ \d{4}$/, '')}{c.parcial ? <span className="block text-[10px] normal-case text-amber-600">parcial</span> : c.enCurso ? <span className="block text-[10px] normal-case text-content-3">en curso</span> : c.sinNominaRegistrada ? <span className="block text-[10px] normal-case text-amber-600">sin nómina</span> : null}
                </th>
              ))}
              <th className="px-2 py-2 text-right font-medium">{d.rotuloAcumulado.replace(/^Acumulado /, 'Acum. ')}</th>
              <th className="hidden px-3 py-2 text-right font-medium sm:table-cell" title={todos ? '% de los ingresos del acumulado' : '% de los ingresos del mes'}>{todos ? '% ingr. acum.' : '% ingresos'}</th>
            </tr>
          </thead>
          <tbody>
            {d.filas.map((f) => {
              const linea = f.tipo === 'linea';
              const conHijos = linea && f.hijos.some((h) => h.acumulado !== 0 || Object.values(h.valores).some((v) => v !== 0));
              const abierta = abiertas.has(f.k);
              const fuerte = !linea;
              const bg = f.tipo === 'resultado' ? 'bg-surface-2/80' : f.tipo === 'subtotal' ? 'bg-surface-2/40' : '';
              return (
                <Fragment key={f.k}>
                  <tr className={`border-b border-line/60 ${bg}`}>
                    <td className={`px-3 py-2 ${fuerte ? 'font-bold text-content' : 'text-content-2'}`}>
                      {conHijos ? (
                        <button onClick={() => toggle(f.k)} className="flex items-center gap-1 text-left hover:text-brand-blue" title={f.nota || ''}>
                          {abierta ? <ChevronDown size={14} /> : <ChevronRight size={14} />}{linea && f.signo && f.signo < 0 ? '− ' : ''}{f.label}
                        </button>
                      ) : <span className={linea ? 'pl-[18px]' : ''} title={f.nota || ''}>{linea && f.signo && f.signo < 0 ? '− ' : fuerte ? '= ' : ''}{f.label}</span>}
                    </td>
                    {cols.map((c) => {
                      const v = f.valores[c.mes];
                      return (
                        <td key={c.mes} className={`px-2 py-2 text-right tabular-nums ${fuerte ? `font-bold ${colorRes(v)}` : 'text-content'}`}>
                          {money(v)}
                          {f.margenes && f.margenes[c.mes] != null && <span className="block text-[11px] font-normal text-content-3">{pctTxt(f.margenes[c.mes])}</span>}
                        </td>
                      );
                    })}
                    <td className={`px-2 py-2 text-right tabular-nums ${fuerte ? `font-bold ${colorRes(f.acumulado)}` : 'font-semibold text-content'}`}>
                      {money(f.acumulado)}
                      {f.margenAcumulado != null && <span className="block text-[11px] font-normal text-content-3">{pctTxt(f.margenAcumulado)}</span>}
                    </td>
                    <td className="hidden px-3 py-2 text-right text-xs tabular-nums text-content-3 sm:table-cell">{f.k === 'ingresos' ? '100 %' : todos ? pctTxt(pctDe(f.acumulado, ing.acumulado)) : pctTxt(pctDe(f.valores[sel.mes], ing.valores[sel.mes]))}</td>
                  </tr>
                  {abierta && f.hijos.filter((h) => h.acumulado !== 0 || Object.values(h.valores).some((v) => v !== 0)).map((h) => (
                    <tr key={f.k + h.nombre} className="border-b border-line/40 bg-surface-2/30 text-xs">
                      <td className="py-1 pl-9 pr-2 text-content-3">{h.nombre}</td>
                      {cols.map((c) => <td key={c.mes} className="px-2 py-1 text-right tabular-nums text-content-3">{h.valores[c.mes] ? money(h.valores[c.mes]) : '—'}</td>)}
                      <td className="px-2 py-1 text-right tabular-nums text-content-2">{money(h.acumulado)}</td>
                      <td className="hidden sm:table-cell" />
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Con nómina calculada: lo que el horario dice y todavía no se registra en Finanzas › Nómina */}
      {(sel.nomina.sinRegistrar > 0 || d.acumulado.nominaSinRegistrar > 0) && (
        <div className={`${card} mt-4 text-sm`}>
          <h3 className="font-display text-base font-bold text-content">Con la nómina calculada</h3>
          <p className="mt-1 text-xs text-content-3">El estado usa la nómina <b>registrada</b> (lo pagado desde Finanzas › Nómina). Si se resta también lo que el horario dice y aún no se registra, queda así:</p>
          <div className="mt-2 space-y-1">
            <div className="flex items-center justify-between border-b border-line/60 py-1.5">
              <span className="text-content-2">Nómina calculada aún no registrada ({d.titulo.toLowerCase()})</span>
              <span className="tabular-nums text-red-700 dark:text-red-300">− {money(sel.nomina.sinRegistrar)}</span>
            </div>
            <div className="flex items-center justify-between border-b border-line/60 py-1.5">
              <span className="font-semibold text-content">Resultado neto con nómina calculada</span>
              <span className={`font-bold tabular-nums ${colorRes(sel.conNominaCalculada.neto)}`}>{money(sel.conNominaCalculada.neto)} <span className="text-xs font-normal text-content-3">{pctTxt(sel.conNominaCalculada.margenNeto)}</span></span>
            </div>
            {d.meses.length > 1 && (
              <div className="flex items-center justify-between py-1.5">
                <span className="text-content-2">{d.rotuloAcumulado}</span>
                <span className={`font-semibold tabular-nums ${colorRes(d.acumulado.conNominaCalculada.neto)}`}>{money(d.acumulado.conNominaCalculada.neto)} <span className="text-xs font-normal text-content-3">{pctTxt(d.acumulado.conNominaCalculada.margenNeto)}</span></span>
              </div>
            )}
          </div>
          <p className="mt-1 text-xs text-content-3">
            Nómina del mes: registrada {money(sel.nomina.registrada)} · calculada {money(sel.nomina.calculada)} (maestros {money(sel.nomina.calculadaDocente)} · administrativa {money(sel.nomina.calculadaAdministrativa)}). El detalle por semana y persona está en la pestaña Conciliación.
          </p>
        </div>
      )}

      <div className={`${card} mt-4 text-xs text-content-3`}>
        <p className="flex items-center gap-1.5">
          {cuadra ? <CheckCircle2 size={14} className="shrink-0 text-emerald-600" /> : <AlertTriangle size={14} className="shrink-0 text-amber-600" />}
          Resultado neto de {d.titulo.toLowerCase()} = «Resultado» de Por apartado ({money(sel.porApartado.resultado)}){cuadra ? ': cuadra.' : ': NO cuadra, avísanos.'}
        </p>
        <button onClick={() => setVerComo(!verComo)} className="mt-2 flex items-center gap-1 font-semibold text-content-2 hover:text-brand-blue">
          {verComo ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Cómo se calcula
        </button>
        {verComo && (
          <ul className="mt-1 list-disc space-y-0.5 pl-5">
            {d.filas.filter((f) => f.nota).map((f) => <li key={f.k}><b className="text-content-2">{f.label}:</b> {f.nota}</li>)}
            {d.supuestos.map((s) => <li key={s}>{s}</li>)}
          </ul>
        )}
        <p className="mt-2">Generado el {d.generado}.</p>
      </div>
    </div>
  );
}
